import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const NotificationFeed = ({
  notifications,
  selectedNotifications,
  onNotificationClick,
  onSelectNotification
}) => {
  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-error/10',
          text: 'text-error',
          icon: 'AlertTriangle',
          badge: 'bg-error text-error-foreground'
        };
      case 'high':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          icon: 'AlertCircle',
          badge: 'bg-warning text-warning-foreground'
        };
      case 'medium':
        return {
          bg: 'bg-accent/10',
          text: 'text-accent',
          icon: 'Info',
          badge: 'bg-accent text-accent-foreground'
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: 'Info',
          badge: 'bg-muted text-muted-foreground'
        };
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'compliance':
        return 'Shield';
      case 'deadline':
        return 'Clock';
      case 'payroll':
        return 'DollarSign';
      case 'widget':
        return 'Activity';
      default:
        return 'Bell';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const now = new Date();
    const diffMs = date - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays <= 7) return `Due in ${diffDays} days`;
    return `Due ${date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
  };

  if (notifications?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <Icon name="CheckCircle" size={64} color="var(--color-success)" className="mb-4" />
        <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
          All caught up!
        </h3>
        <p className="text-sm text-muted-foreground text-center">
          No notifications match your current filters.
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {notifications?.map((notification) => {
        const styles = getSeverityStyles(notification?.severity);
        const isSelected = selectedNotifications?.includes(notification?.id);
        const isUnread = notification?.status === 'unread' || !notification?.readAt;

        return (
          <div
            key={notification?.id}
            className={`p-4 sm:p-5 hover:bg-muted/50 transition-smooth ${
              isUnread ? 'bg-accent/5' : ''
            } ${isSelected ? 'bg-primary/5' : ''}`}
          >
            <div className="flex items-start gap-3 sm:gap-4">
              {/* Checkbox */}
              <div className="pt-1.5 flex-shrink-0">
                <Checkbox
                  checked={isSelected}
                  onChange={() => onSelectNotification?.(notification?.id)}
                />
              </div>

              {/* Category Icon */}
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${styles?.bg} flex-shrink-0`}>
                <Icon name={getCategoryIcon(notification?.category)} size={20} color={`var(--color-${styles?.text?.replace('text-', '')})`} />
              </div>

              {/* Content */}
              <div 
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => onNotificationClick?.(notification)}
              >
                {/* Header Row */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <h4 className="font-body font-semibold text-sm text-foreground break-words">
                      {notification?.title}
                    </h4>
                    <span className={`px-2 py-0.5 text-xs font-caption rounded-full ${styles?.badge} whitespace-nowrap flex-shrink-0`}>
                      {notification?.severity}
                    </span>
                    {notification?.category && (
                      <span className="px-2 py-0.5 text-xs font-caption bg-muted text-muted-foreground rounded-full capitalize whitespace-nowrap flex-shrink-0">
                        {notification?.category}
                      </span>
                    )}
                  </div>
                  {isUnread && (
                    <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5"></span>
                  )}
                </div>

                {/* Message */}
                <p className="text-sm text-muted-foreground mb-3 break-words overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>
                  {notification?.message}
                </p>

                {/* Metadata Row */}
                <div className="flex items-center gap-3 sm:gap-4 flex-wrap text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <Icon name="Clock" size={14} color="currentColor" className="flex-shrink-0" />
                    <span>{formatTimestamp(notification?.createdAt)}</span>
                  </div>

                  {notification?.department && (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Icon name="Building" size={14} color="currentColor" className="flex-shrink-0" />
                      <span className="truncate">{notification?.department}</span>
                    </div>
                  )}

                  {notification?.affectedCount > 0 && (
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <Icon name="Users" size={14} color="currentColor" className="flex-shrink-0" />
                      <span>{notification?.affectedCount} affected</span>
                    </div>
                  )}

                  {notification?.deadline && (
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <Icon name="Calendar" size={14} color="currentColor" className="flex-shrink-0" />
                      <span className={notification?.metadata?.daysUntilDue <= 3 ? 'text-error font-semibold' : ''}>
                        {formatDeadline(notification?.deadline)}
                      </span>
                    </div>
                  )}

                  {notification?.estimatedCost > 0 && (
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <Icon name="DollarSign" size={14} color="currentColor" className="flex-shrink-0" />
                      <span>K{notification?.estimatedCost?.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Severity Icon */}
              <div className={`${styles?.text} flex-shrink-0`}>
                <Icon name={styles?.icon} size={20} color="currentColor" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationFeed;
